import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ExternalLink,
  ListFilter,
  PackageOpen,
  Search,
  X
} from "lucide-react";
import type {
  ConditionFilter,
  PriceScopeFilter,
  PriceTypeFilter,
  SalesFilterOptions,
  SalesFiltersState,
  SalesListing,
  SalesSort,
  SalesStatus,
  SortDirection
} from "../types";
import { isFiftyPercentOff } from "../sales-snapshot";

const date = new Intl.DateTimeFormat("ru-RU", { day: "2-digit", month: "short", year: "numeric" });
const compactDate = new Intl.DateTimeFormat("ru-RU", { day: "2-digit", month: "short" });

const conditionOptions: Array<{ value: ConditionFilter; label: string }> = [
  { value: "new", label: "Новый" },
  { value: "open_box", label: "Открытая коробка / категория B" },
  { value: "refurbished", label: "Восстановленный" },
  { value: "used", label: "Б/у" },
  { value: "for_parts", label: "Неисправный / на запчасти" },
  { value: "unknown", label: "Не определено" }
];

const priceTypeOptions: Array<{ value: PriceTypeFilter; label: string }> = [
  { value: "fixed", label: "Фиксированная" },
  { value: "negotiable", label: "Торг" },
  { value: "unknown", label: "Не определено" }
];

const priceScopeOptions: Array<{ value: PriceScopeFilter; label: string }> = [
  { value: "item", label: "Товар" },
  { value: "lot", label: "Лот" }
];

export function formatPrice(amount: number | null | undefined) {
  if (typeof amount !== "number" || !Number.isFinite(amount)) return "—";
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

export function priceDifference(price: number | null | undefined, referencePrice: number | null | undefined) {
  if (
    typeof price !== "number" || !Number.isFinite(price)
    || typeof referencePrice !== "number" || !Number.isFinite(referencePrice)
    || price <= 0 || referencePrice <= 0
  ) return null;
  return price - referencePrice;
}

// Interface-only color rule: zero through 10% of the compared listing price is yellow.
export function priceTone(price: number | null | undefined, difference: number | null) {
  if (difference === null || typeof price !== "number" || !Number.isFinite(price) || price <= 0) return "";
  if (difference < 0) return "is-favorable";
  return difference <= price * 0.1 ? "is-near-target" : "is-unfavorable";
}

export function formatPriceDifference(difference: number) {
  if (difference > 0) return `+${formatPrice(difference)}`;
  if (difference < 0) return `−${formatPrice(Math.abs(difference))}`;
  return formatPrice(0);
}

export function formatDate(value: string | null) {
  if (!value) return "—";
  const parsed = new Date(value);
  return Number.isNaN(parsed.valueOf()) ? "—" : date.format(parsed);
}

function formatCompactDate(value: string) {
  const parsed = new Date(value);
  return Number.isNaN(parsed.valueOf()) ? "—" : compactDate.format(parsed);
}

const conditionLabels: Record<string, string> = {
  new: "Новый",
  open_box: "Открытая коробка",
  refurbished: "Восстановленный",
  used: "Б/у",
  for_parts: "На запчасти",
  unknown: "Не определено"
};

export function formatCondition(value: string | null | undefined) {
  return conditionLabels[value || "unknown"] || value || conditionLabels.unknown;
}

interface HeaderFilterProps {
  label: string;
  active: boolean;
  children: ReactNode;
}

function HeaderFilter({ label, active, children }: HeaderFilterProps) {
  const details = useRef<HTMLDetailsElement>(null);
  const summary = useRef<HTMLElement>(null);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);

  useEffect(() => {
    const close = (event?: Event) => {
      if (event?.target instanceof Node && details.current?.contains(event.target)) return;
      details.current?.removeAttribute("open");
      setPosition(null);
    };
    document.addEventListener("pointerdown", close);
    window.addEventListener("resize", close);
    window.addEventListener("scroll", close, true);
    return () => {
      document.removeEventListener("pointerdown", close);
      window.removeEventListener("resize", close);
      window.removeEventListener("scroll", close, true);
    };
  }, []);

  const positionMenu = () => {
    if (!details.current?.open || !summary.current) {
      setPosition(null);
      return;
    }
    const rect = summary.current.getBoundingClientRect();
    const width = 280;
    setPosition({
      top: rect.bottom + 6,
      left: Math.max(8, Math.min(rect.left, window.innerWidth - width - 8))
    });
  };

  return <details className="header-filter" ref={details} onToggle={positionMenu}>
    <summary ref={summary} aria-label={label} title={label} className={active ? "is-active" : ""}>
      <ListFilter size={14} />
    </summary>
    <div
      className="header-filter-menu"
      style={position ? { top: position.top, left: position.left } : { visibility: "hidden" }}
    >{children}</div>
  </details>;
}

interface CheckboxGroupProps<T extends string> {
  title?: string;
  allLabel: string;
  value: T[] | null;
  options: Array<{ value: T; label: string }>;
  onChange: (value: T[] | null) => void;
}

export function nextCheckboxGroupValue<T extends string>(
  value: T[] | null,
  options: readonly T[],
  option: T,
  checked: boolean
): T[] | null {
  const current = value === null ? [...options] : value;
  const next = checked
    ? [...new Set([...current, option])]
    : current.filter(item => item !== option);
  return next.length === options.length ? null : options.filter(item => next.includes(item));
}

function CheckboxGroup<T extends string>({ title, allLabel, value, options, onChange }: CheckboxGroupProps<T>) {
  const selected = value === null ? options.map(option => option.value) : value;
  const toggle = (option: T, checked: boolean) => {
    onChange(nextCheckboxGroupValue(value, options.map(item => item.value), option, checked));
  };

  return <div className="header-filter-group">
    {title && <strong>{title}</strong>}
    <label className="header-filter-option header-filter-option-all">
      <input type="checkbox" checked={value === null} onChange={event => onChange(event.target.checked ? null : [])} />
      <span>{allLabel}</span>
    </label>
    {options.map(option => <label className="header-filter-option" key={option.value}>
      <input
        type="checkbox"
        checked={selected.includes(option.value)}
        onChange={event => toggle(option.value, event.target.checked)}
      />
      <span>{option.label}</span>
    </label>)}
  </div>;
}

interface SortHeaderProps {
  label: string;
  field: SalesSort;
  filters: SalesFiltersState;
  menuLabel?: string;
  ascendingLabel: string;
  descendingLabel: string;
  onChange: (patch: Partial<SalesFiltersState>) => void;
}

function SortHeader({ label, field, filters, menuLabel = "Порядок", ascendingLabel, descendingLabel, onChange }: SortHeaderProps) {
  const details = useRef<HTMLDetailsElement>(null);
  const summary = useRef<HTMLElement>(null);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const active = filters.sort === field;

  useEffect(() => {
    const close = (event?: Event) => {
      if (event?.target instanceof Node && details.current?.contains(event.target)) return;
      details.current?.removeAttribute("open");
      setPosition(null);
    };
    document.addEventListener("pointerdown", close);
    window.addEventListener("resize", close);
    window.addEventListener("scroll", close, true);
    return () => {
      document.removeEventListener("pointerdown", close);
      window.removeEventListener("resize", close);
      window.removeEventListener("scroll", close, true);
    };
  }, []);

  const positionMenu = () => {
    if (!details.current?.open || !summary.current) {
      setPosition(null);
      return;
    }
    const rect = summary.current.getBoundingClientRect();
    const width = 230;
    setPosition({
      top: rect.bottom + 6,
      left: Math.max(8, Math.min(rect.left, window.innerWidth - width - 8))
    });
  };

  const select = (direction: SortDirection) => {
    onChange({ sort: field, direction, page: 1 });
    details.current?.removeAttribute("open");
    setPosition(null);
  };

  const currentLabel = active
    ? filters.direction === "asc" ? ascendingLabel : descendingLabel
    : "Выбрать порядок";

  return <details className="table-sort-menu" ref={details} onToggle={positionMenu}>
    <summary
      ref={summary}
      className={`table-sort-button ${active ? "is-active" : ""}`}
      title={`${label}: ${currentLabel}`}
      aria-label={`Сортировка: ${label}. ${currentLabel}`}
    >
      <span>{label}</span>
      {active
        ? filters.direction === "asc" ? <ArrowUp size={14} /> : <ArrowDown size={14} />
        : <ArrowUpDown size={14} />}
    </summary>
    <div
      className="header-filter-menu header-sort-options"
      style={position ? { top: position.top, left: position.left } : { visibility: "hidden" }}
    >
      <strong>{menuLabel}</strong>
      <button
        type="button"
        className={active && filters.direction === "asc" ? "is-selected" : ""}
        onClick={() => select("asc")}
      >
        <ArrowUp size={15} />
        <span>{ascendingLabel}</span>
      </button>
      <button
        type="button"
        className={active && filters.direction === "desc" ? "is-selected" : ""}
        onClick={() => select("desc")}
      >
        <ArrowDown size={15} />
        <span>{descendingLabel}</span>
      </button>
    </div>
  </details>;
}

interface SalesTableProps {
  items: SalesListing[];
  filters: SalesFiltersState;
  filterOptions: SalesFilterOptions;
  busy: boolean;
  onChange: (patch: Partial<SalesFiltersState>) => void;
  onStatusChange: (item: SalesListing, status: SalesStatus) => void;
  statusUpdates: Record<string, SalesStatus>;
  statusErrors: Record<string, string>;
  statusDisabled: boolean;
}

export function SalesTable({ items, filters, filterOptions, busy, onChange, onStatusChange, statusUpdates, statusErrors, statusDisabled }: SalesTableProps) {
  const sourceOptions = filterOptions.sources.map(source => ({ value: source, label: source.replace(/\.sqlite$/i, "") }));
  const availableConditionOptions = conditionOptions.filter(option => filterOptions.conditions.includes(option.value));
  const availablePriceTypeOptions = priceTypeOptions.filter(option => filterOptions.priceTypes.includes(option.value));
  const availablePriceScopeOptions = priceScopeOptions.filter(option => filterOptions.priceScopes.includes(option.value));
  return <div className="table-scroll">
    <table className="sales-table">
      <thead>
        <tr>
          <th><div className="table-header-controls">
            <SortHeader label="Площадка" field="source" filters={filters} ascendingLabel="От А до Я" descendingLabel="От Я до А" onChange={onChange} />
            <HeaderFilter label="Фильтр площадок" active={filters.sources !== null}>
              <CheckboxGroup allLabel="Все площадки" value={filters.sources} options={sourceOptions} onChange={value => onChange({ sources: value, page: 1 })} />
            </HeaderFilter>
          </div></th>
          <th><div className="table-header-controls">
            <SortHeader label="Объявление" field="title" filters={filters} ascendingLabel="От А до Я" descendingLabel="От Я до А" onChange={onChange} />
            <HeaderFilter label="Поиск объявлений" active={Boolean(filters.q)}>
              <div className="header-search">
                <Search size={15} />
                <input
                  value={filters.q}
                  onChange={event => onChange({ q: event.target.value, page: 1 })}
                  placeholder="Название, ID или MPN"
                  maxLength={160}
                />
                {filters.q && <button type="button" onClick={() => onChange({ q: "", page: 1 })} aria-label="Очистить поиск"><X size={14} /></button>}
                {busy && <i className="header-search-progress" />}
              </div>
            </HeaderFilter>
          </div></th>
          <th><div className="table-header-controls">
            <SortHeader label="Цена" field="profitability" filters={filters} menuLabel="Выгодность" ascendingLabel="Лучшее сначала" descendingLabel="Худшее сначала" onChange={onChange} />
            <HeaderFilter label="Фильтры цены" active={filters.priceView === "off50" || filters.priceTypes !== null || filters.priceScopes !== null}>
              <div className="header-filter-group" role="group" aria-label="Список предложений">
                <strong>Список</strong>
                <label className="header-filter-option"><input type="radio" name="price-view" checked={filters.priceView === "regular"} onChange={() => onChange({ priceView: "regular", page: 1 })} /><span>Обычные</span></label>
                <label className="header-filter-option"><input type="radio" name="price-view" checked={filters.priceView === "off50"} onChange={() => onChange({ priceView: "off50", page: 1 })} /><span>50% off · разница больше половины цены</span></label>
              </div>
              <CheckboxGroup title="Тип цены" allLabel="Любой тип" value={filters.priceTypes} options={availablePriceTypeOptions} onChange={value => onChange({ priceTypes: value, page: 1 })} />
              <CheckboxGroup title="Цена за" allLabel="Любой вариант" value={filters.priceScopes} options={availablePriceScopeOptions} onChange={value => onChange({ priceScopes: value, page: 1 })} />
            </HeaderFilter>
          </div></th>
          <th><div className="table-header-controls">
            <SortHeader label="Состояние" field="condition" filters={filters} ascendingLabel="Новые сначала" descendingLabel="Не определено сначала" onChange={onChange} />
            <HeaderFilter label="Фильтр состояния" active={filters.conditions !== null}>
              <CheckboxGroup allLabel="Все состояния" value={filters.conditions} options={availableConditionOptions} onChange={value => onChange({ conditions: value, page: 1 })} />
            </HeaderFilter>
          </div></th>
          <th>Связанные MPN</th>
          <th>Статус</th>
          <th><SortHeader label="Найдено" field="added" filters={filters} ascendingLabel="Сначала старые" descendingLabel="Сначала новые" onChange={onChange} /></th>
        </tr>
      </thead>
      <tbody>
        {items.map(item => {
          const difference = priceDifference(item.comparisonPriceEur, item.referencePriceEur);
          const differenceTone = priceTone(item.comparisonPriceEur, difference);
          return <tr key={item.key}>
            <td><span className="source-badge"><i />{item.source}</span></td>
            <td className="listing-column">
              <div className="listing-title-row">
                {item.url
                  ? <a href={item.url} target="_blank" rel="noopener noreferrer">{item.title || "Без названия"}<ExternalLink size={13} /></a>
                  : <strong>{item.title || "Без названия"}</strong>}
              </div>
              <span className="listing-id">ID {item.listingId}</span>
              {item.lastConfirmedAt && <span className="listing-id">Проверено {formatDate(item.lastConfirmedAt)}</span>}
            </td>
            <td className={`price-cell ${differenceTone}`}>
              <strong className="price">{formatPrice(item.priceEur)}</strong>
              {item.comparisonPriceEur !== null && item.priceEur !== item.comparisonPriceEur &&
                <span className="price-difference">для сравнения {formatPrice(item.comparisonPriceEur)}</span>}
              {difference !== null && <span
                className="price-difference"
                title={`Целевая закупочная цена: ${formatPrice(item.referencePriceEur)}`}
              >цель {formatPrice(item.referencePriceEur)} · {formatPriceDifference(difference)}</span>}
              {item.priceEur !== null && item.comparisonPriceEur === null &&
                <span className="price-difference">НДС не уточнён · сравнение недоступно</span>}
            </td>
            <td><span className={`condition-badge condition-${item.condition || "unknown"}`}>{formatCondition(item.condition)}</span>{isFiftyPercentOff(item) && <span className="price-flag-badge">50% off</span>}</td>
            <td>
              {item.mpns.length
                ? <div className="mpn-list">{item.mpns.map(mpn => <span key={mpn} title={mpn}>{mpn}</span>)}</div>
                : <span className="muted"><PackageOpen size={14} />—</span>}
            </td>
            <td className="sales-status-cell">
              <select
                aria-label={`Статус объявления ${item.listingId}`}
                value={String((Object.hasOwn(statusUpdates, item.key) ? statusUpdates[item.key] : item.salesStatus) ?? "")}
                disabled={statusDisabled || Object.hasOwn(statusUpdates, item.key)}
                onChange={event => onStatusChange(item, event.target.value === "" ? null : Number(event.target.value) as SalesStatus)}
              >
                <option value="">—</option>
                <option value="0">0</option>
                <option value="1">1</option>
                <option value="2">2</option>
                <option value="3">3</option>
              </select>
              {Object.hasOwn(statusUpdates, item.key) && <small>Сохраняется…</small>}
              {statusErrors[item.key] && <small className="sales-status-error" role="alert" title={statusErrors[item.key]}>Не сохранено</small>}
            </td>
            <td>{item.capturedAt
              ? <time dateTime={item.capturedAt} title={formatDate(item.capturedAt)}>{formatCompactDate(item.capturedAt)}</time>
              : <span className="muted">—</span>}
            </td>
          </tr>;
        })}
      </tbody>
    </table>
  </div>;
}
